import { useState, useCallback, useEffect } from 'react'
import { useTheme } from '@mui/material'
import moment from 'moment'
import { apiGet } from '../../../../generic/Api_Functions'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { getStatusConfig } from '../../DeviceSelector/SingleDeviceSelector/utils/constants'
import { durationStringToMilliseconds } from '../../../../generic/DateTimeFunctions'

/**
 * Hook to fetch and manage monitoring data.
 * @param {object} device - The device object.
 * @returns {object} The monitoring data and loading state.
 */
/**
 * Custom hook to handle chart data logic.
 * @param {Object} config - Widget configuration.
 * @param {string} config.time - Time range for the query form now.
 * @param {string} config.startTime - Start time for the query.
 * @param {string} config.endTime - End time for the query.
 */
export const useMonitoringData = (device, timeData) => {
  const theme = useTheme()
  const { t_ } = useSmartTranslation()
  const [data, setData] = useState(null)
  const [ofSegments, setOfSegments] = useState([])
  const [loading, setLoading] = useState(true)

  const loadInfo = useCallback(async () => {
    const getRange = () => {
      const mode = timeData?.timeRangeMode || 'last'
      if (mode === 'range' && timeData?.startTime && timeData?.endTime) {
        return {
          startDate: moment(timeData.startTime).toISOString(),
          endDate: moment(timeData.endTime).toISOString()
        }
      }

      let durationMs = 24 * 60 * 60 * 1000
      if (timeData?.time) {
        durationMs = durationStringToMilliseconds(timeData.time)
      }
      const endMoment = moment()
      const startMoment = moment(endMoment).subtract(durationMs, 'milliseconds')

      return {
        startDate: startMoment.toISOString(),
        endDate: endMoment.toISOString()
      }
    }

    if (!device?.id) return null
    const { startDate, endDate } = getRange()

    const timelinePromise = apiGet(`mos/deviceTimeline/${device.id}?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`).catch(err => {
      console.error('Error fetching timeline data', err)
      return null
    })

    const ofTimelinePromise = apiGet(`mos/deviceOfTimeline/${device.id}?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`)
      .then(res => {
        return res
      })
      .catch(err => {
        console.error('Error fetching Device OF timeline data', err)
        return null
      })
      // test status timeline
    const statusTimelinePromise = apiGet(`mos/deviceStatusTimeline/${device.id}?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`)
      .then(res => {
        return res
      })
      .catch(err => {
        console.error('Error fetching Device Status timeline data', err)
        return null
      })

    const [timeline, ofTimeline, statusTimeline] = await Promise.all([timelinePromise, ofTimelinePromise, statusTimelinePromise])
    return { timeline, ofTimeline, statusTimeline }
  }, [device?.id, timeData])

  const updateInfo = useCallback((result) => {
    if (!result) return
    const { timeline, ofTimeline, statusTimeline } = result

    // Process Timeline Data
    let newOperators = null
    if (timeline) {
      if (Array.isArray(timeline)) {
        newOperators = timeline.map(item => ({
          start: item.start,
          end: item.end,
          label: item.worker ? `${item.worker.code} - ${item.worker.name}` : t_('Unknown'),
          color: theme.palette.primary.light
        }))
      }
    }

    // Process Status Timeline Data
    let newMachineStatus = null
    if (statusTimeline && Array.isArray(statusTimeline)) {
      newMachineStatus = statusTimeline.map(item => {
        const rawStatus = item?.deviceStatus ?? item?.status ?? item?.state
        const config = getStatusConfig(rawStatus, theme, t_)
        return {
          start: item.startDate ?? item.start,
          end: item.endDate ?? item.end,
          label: config.label,
          color: config.text
        }
      })
    }

    if (newOperators || newMachineStatus) {
      setData(prevData => ({
        ...(prevData || {}),
        ...(newOperators && { operators: newOperators }),
        ...(newMachineStatus && { machineStatus: newMachineStatus })
      }))
    } else if (timeline && !Array.isArray(timeline)) {
      // Fallback if timeline returns full data object directly (legacy support?)
      setData(timeline)
    }

    // Process OF Timeline Data
    if (ofTimeline) {
      if (Array.isArray(ofTimeline)) {
        const mapped = ofTimeline.map(seg => ({
          fabricationOrder: seg.fabricationOrder || seg.of || seg.ofId
            ? {
                id: seg.fabricationOrder?.id ?? seg.of?.id ?? seg.ofId ?? seg.of_id ?? seg.ofCode ?? seg.of_code ?? seg.label,
                code: seg.fabricationOrder?.code ?? seg.of?.code ?? seg.ofCode ?? seg.of_code ?? seg.label,
                name: seg.fabricationOrder?.name ?? seg.of?.name ?? seg.ofName ?? seg.of_name ?? seg.label
              }
            : undefined,
          jobReference: seg.jobReference || seg.job || seg.jobId
            ? {
                id: seg.jobReference?.id ?? seg.job?.id ?? seg.jobId ?? seg.job_id ?? seg.jobCode ?? seg.job_code ?? seg.label,
                code: seg.jobReference?.code ?? seg.job?.code ?? seg.jobCode ?? seg.job_code ?? seg.label,
                name: seg.jobReference?.name ?? seg.job?.name ?? seg.jobName ?? seg.job_name ?? seg.label
              }
            : undefined,
          status: seg.status ?? seg.state ?? undefined,
          statusType: seg.statusType ?? seg.stateType ?? undefined,
          start: seg.start,
          end: seg.end
        }))
        setOfSegments(mapped)
      } else {
        setOfSegments([])
      }
    }
    setLoading(false)
  }, [theme, t_])

  useEffect(() => {
    loadInfo().then(updateInfo)
  }, [loadInfo, updateInfo])

  return { data, ofSegments, loading, loadInfo, updateInfo }
}
